import { Copy, Trash2, Lock, Unlock, Eye, EyeOff, Settings, type LucideIcon } from 'lucide-react'
import type { Persona } from '@/stores'

export type PersonaCardMenuKey = 'settings' | 'duplicate' | 'archive' | 'hard-delete' | 'lock' | 'admin-unlock' | 'hide' | 'unhide'

export interface PersonaCardMenuItem {
  key: PersonaCardMenuKey
  label: string
  icon: LucideIcon
  onSelect: () => void
  danger?: boolean
}

export interface BuildPersonaCardMenuArgs {
  persona: Persona
  isArchived: boolean
  canArchive: boolean
  canDuplicate: boolean
  isAdmin: boolean
  /** True when the current user owns this persona (only owners may lock it). */
  isOwner: boolean
  /** True when the persona is orchestrator-managed (source != "manual"). */
  isManaged: boolean
  /** True when a non-owner admin may break-glass unlock this (locked) persona. */
  canAdminUnlock: boolean
  /** True when this persona is in the current user's hidden set. */
  isHidden: boolean
  /** True when the current user may edit this persona (gates "Settings…"). */
  canEdit: boolean
  onDuplicate: (p: Persona) => void
  onArchive: (p: Persona) => void
  onHardDelete: (p: Persona) => void
  onToggleLock: (p: Persona) => void
  onAdminUnlock: (p: Persona) => void
  onToggleHidden: (p: Persona) => void
  onOpenSettings: (p: Persona) => void
}

/**
 * Secondary card actions for the overflow (⋯) menu. Edit (→ the Workshop) is
 * the card's visible button; infra configuration lives here as "Settings…",
 * which opens the Persona Settings modal.
 */
export function buildPersonaCardMenuItems(args: BuildPersonaCardMenuArgs): PersonaCardMenuItem[] {
  const { persona, isArchived } = args
  const items: PersonaCardMenuItem[] = []

  if (isArchived) {
    if (args.isAdmin && args.canArchive) {
      items.push({
        key: 'hard-delete', label: 'Delete permanently', icon: Trash2, danger: true,
        onSelect: () => args.onHardDelete(persona),
      })
    }
    return items
  }

  if (args.canEdit) {
    items.push({ key: 'settings', label: 'Settings…', icon: Settings, onSelect: () => args.onOpenSettings(persona) })
  }

  if (args.canDuplicate) {
    items.push({ key: 'duplicate', label: 'Duplicate', icon: Copy, onSelect: () => args.onDuplicate(persona) })
  }
  // Lock/unlock (freeze a canonical persona) is owner-only, and doesn't apply
  // to orchestrator-managed personas.
  if (args.isOwner && !args.isManaged) {
    const locked = persona.isLocked === true
    items.push({
      key: 'lock',
      label: locked ? 'Unlock editing' : 'Lock editing',
      icon: locked ? Unlock : Lock,
      onSelect: () => args.onToggleLock(persona),
    })
  } else if (args.canAdminUnlock) {
    // Non-owner admins can't lock, but can break-glass unlock a frozen persona.
    items.push({
      key: 'admin-unlock',
      label: 'Unlock (admin)',
      icon: Unlock,
      onSelect: () => args.onAdminUnlock(persona),
    })
  }

  // Per-user hide (cosmetic declutter) — available on every persona, owned
  // or not; the hidden set lives in the user's settings, not the persona row.
  items.push(
    args.isHidden
      ? { key: 'unhide', label: 'Unhide', icon: Eye, onSelect: () => args.onToggleHidden(persona) }
      : { key: 'hide', label: 'Hide from my list', icon: EyeOff, onSelect: () => args.onToggleHidden(persona) },
  )

  if (args.canArchive) {
    items.push({ key: 'archive', label: 'Archive', icon: Trash2, danger: true, onSelect: () => args.onArchive(persona) })
  }
  return items
}
